import * as React from 'react';
import { useCognitoContext } from '../../providers/cognito.provider';
import type { PasswordResetFormValues } from '@components/organisms/reset-password-form';
import type { ResponseResult } from '@utils';

type Step = 'choose-email' | 'reset-password';

type Props = {
	onPasswordReset: () => void;
	initialEmail?: string;
	autoSend?: boolean;
};

export const useResetPassword = ({ onPasswordReset, initialEmail, autoSend }: Props) => {
	const { forgotPassword, resetPassword } = useCognitoContext();

	const [step, setStep] = React.useState<Step>('choose-email');
	const [email, setEmail] = React.useState<string>(initialEmail?.trim() ?? '');
	const [autoSendError, setAutoSendError] = React.useState<string | undefined>();
	const [isAutoSending, setIsAutoSending] = React.useState(() => Boolean(autoSend && initialEmail?.trim()));
	const autoSendStarted = React.useRef(false);

	const sendResetPasswordRequest = React.useCallback(
		async ({ email: nextEmail }: { email: string }): Promise<ResponseResult<unknown>> => {
			const trimmed = nextEmail.trim();
			const response = await forgotPassword(trimmed);

			if (response.error) {
				return response;
			}

			setEmail(trimmed);
			setAutoSendError(undefined);
			setStep('reset-password');

			return response;
		},
		[forgotPassword]
	);

	React.useEffect(() => {
		const trimmed = email.trim();
		if (!autoSend || !trimmed || autoSendStarted.current) {
			return;
		}

		autoSendStarted.current = true;
		setIsAutoSending(true);

		void (async () => {
			try {
				const response = await sendResetPasswordRequest({ email: trimmed });
				if (response.error) {
					setAutoSendError(response.error.message);
				}
			} finally {
				setIsAutoSending(false);
			}
		})();
	}, [autoSend, email, sendResetPasswordRequest]);

	const resendCode = React.useCallback(async (): Promise<ResponseResult<unknown>> => {
		return await forgotPassword(email);
	}, [forgotPassword, email]);

	const handleResetPassword = React.useCallback(
		async ({ code, password }: PasswordResetFormValues) => {
			const response = await resetPassword(email, code, password);

			if (response.error) {
				return response;
			}

			onPasswordReset?.();
			return response;
		},
		[resetPassword, email, onPasswordReset]
	);

	return {
		step,
		email,
		autoSendError,
		isAutoSending,
		resendCode,
		handleResetPassword,
		sendResetPasswordRequest,
	};
};
